<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-3 sm:p-4">
    <div class="bg-white rounded-3xl max-w-2xl w-full shadow-2xl relative overflow-hidden animate-fade-in my-auto max-h-[88vh] flex flex-col">
      <!-- Sticky Header -->
      <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-white">
        <div>
          <h2 class="text-lg font-bold text-gray-900">
            {{ employee ? 'Editar Personal' : 'Registrar Nuevo Personal' }}
          </h2>
          <p class="text-xs text-gray-500">Datos personales, modalidad de pago e información bancaria.</p>
        </div>
        <button 
          type="button"
          class="text-gray-400 hover:text-gray-600 text-lg p-1 transition-colors cursor-pointer" 
          @click="$emit('close')"
        >
          <i class="fa fa-times" />
        </button>
      </div>

      <!-- Scrollable Form Body -->
      <form id="employeeFormElement" class="flex-1 overflow-y-auto px-6 py-4 space-y-3.5" @submit.prevent="handleSubmit">
        <!-- Datos Básicos -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Nombre Completo *</label>
            <input
              v-model="form.name"
              type="text"
              required
              placeholder="Ej. Juan Pérez"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Cédula / DNI / Identificación</label>
            <input
              v-model="form.identification"
              type="text"
              placeholder="V-12345678"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Teléfono / WhatsApp</label>
            <input
              v-model="form.phone"
              type="text"
              placeholder="0414-1234567"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Correo Electrónico</label>
            <input
              v-model="form.email"
              type="email"
              placeholder="correo@ejemplo.com"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Ubicación / Ciudad / Región</label>
            <input
              v-model="form.location"
              type="text"
              placeholder="Ej. Caracas, Valencia, Maturín..."
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Cargo / Función</label>
            <input
              v-model="form.role_or_job"
              type="text"
              placeholder="Ej. Cocinero, Conductor, Operario"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1 flex items-center justify-between">
              <span class="flex items-center gap-1.5">
                <i class="fa fa-folder text-purple-600" />
                <span>Evento / Proyecto Asignado</span>
              </span>
              <span class="text-[10px] text-gray-400 font-normal">Opcional</span>
            </label>
            <input
              v-model="form.event_name"
              type="text"
              list="modal-employee-events-list"
              placeholder="Ej. Concierto Caracas, Expo..."
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-purple-500 focus:border-purple-500"
            />
            <datalist id="modal-employee-events-list">
              <option v-for="ev in events" :key="ev" :value="ev">{{ ev }}</option>
            </datalist>
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Estado</label>
            <select
              v-model="form.status"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-bold"
            >
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
        </div>

        <!-- Modalidad & Tarifa -->
        <div class="bg-orange-50/60 p-3.5 rounded-xl border border-orange-200/80">
          <h3 class="text-xs font-bold uppercase tracking-wider text-orange-900 mb-2 flex items-center gap-1.5">
            <i class="fa fa-dollar-sign text-orange-600" />
            Modalidad & Tarifa
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Modalidad de Pago *</label>
              <select
                v-model="form.type_salary"
                required
                class="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-orange-500 focus:border-orange-500 font-medium"
              >
                <option value="shift">Por Turno (Diurno / Nocturno / Jornada)</option>
                <option value="piecework">Trabajo a Destajo / Producción / Tarea</option>
                <option value="hourly">Por Hora Trabajada</option>
                <option value="fixed">Sueldo Fijo Mensual / Quincenal</option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Tarifa Base ($ USD) *</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="font-bold text-gray-400 text-sm leading-none">$</span>
                </div>
                <input
                  v-model.number="form.rate"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  placeholder="0.00"
                  class="w-full pl-7 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-900 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <p class="text-[10px] text-gray-600 mt-0.5 font-medium">Aprox. {{ formatBs(form.rate || 0) }}</p>
            </div>
          </div>
        </div>

        <!-- Información Bancaria -->
        <div class="bg-gray-50 p-3.5 rounded-xl border border-gray-200">
          <h3 class="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2 flex items-center gap-1.5">
            <i class="fa fa-university text-gray-500" />
            Información Bancaria (Para Transferencias)
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Banco</label>
              <input
                v-model="form.bank_name"
                type="text"
                placeholder="Ej. Banesco, Banco de Venezuela, Mercantil"
                class="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Tipo de Cuenta</label>
              <select
                v-model="form.account_type"
                class="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="Corriente">Corriente</option>
                <option value="Ahorro">Ahorro</option>
                <option value="Zelle">Zelle / Correo</option>
                <option value="Otro">Otro</option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Número de Cuenta (20 dígitos)</label>
              <input
                v-model="form.account_number"
                type="text"
                placeholder="0102 0000 00 0000000000"
                class="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-mono focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Datos Pago Móvil (Teléfono y Cédula)</label>
              <input
                v-model="form.payment_identification"
                type="text"
                placeholder="Ej. 0424-1234567 / V-12345678"
                class="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>
        </div>

        <!-- Notas u Observaciones -->
        <div>
          <label class="block text-xs font-bold text-gray-700 mb-1">Notas u Observaciones</label>
          <input
            v-model="form.notes"
            type="text"
            placeholder="Observaciones adicionales sobre el personal..."
            class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
      </form>

      <!-- Sticky Footer -->
      <div class="px-6 py-3.5 bg-gray-50 border-t border-gray-100 flex-shrink-0 flex items-center justify-end gap-3 rounded-b-3xl">
        <button
          type="button"
          class="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-medium text-xs hover:bg-gray-100 transition-colors cursor-pointer"
          @click="$emit('close')"
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="employeeFormElement"
          :disabled="submitting"
          class="px-5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium text-xs shadow-md transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
        >
          {{ submitting ? 'Guardando...' : (employee ? 'Actualizar Personal' : 'Registrar Personal') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmployeeModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    employee: {
      type: Object,
      default: null,
    },
    events: {
      type: Array,
      default: () => [],
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    currentExchangeRate: {
      type: Number,
      default: 1.0,
    },
  },

  emits: ['close', 'submit'],

  data() {
    return {
      form: this.getInitialFormData(),
    }
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.form = this.getInitialFormData()
      }
    },
    employee(emp) {
      if (emp) {
        this.form = {
          name: emp.name || '',
          identification: emp.identification || '',
          phone: emp.phone || '',
          email: emp.email || '',
          location: emp.location || '',
          role_or_job: emp.role_or_job || '',
          event_name: emp.event_name || '',
          type_salary: emp.type_salary || 'shift',
          rate: emp.rate || 0,
          bank_name: emp.bank_name || '',
          account_type: emp.account_type || 'Corriente',
          account_number: emp.account_number || '',
          payment_identification: emp.payment_identification || '',
          status: emp.status || 'active',
          notes: emp.notes || '',
        }
      } else {
        this.form = this.getInitialFormData()
      }
    },
  },

  methods: {
    getInitialFormData() {
      if (this.employee) {
        return {
          name: this.employee.name || '',
          identification: this.employee.identification || '',
          phone: this.employee.phone || '',
          email: this.employee.email || '',
          location: this.employee.location || '',
          role_or_job: this.employee.role_or_job || '',
          event_name: this.employee.event_name || '',
          type_salary: this.employee.type_salary || 'shift',
          rate: this.employee.rate || 0,
          bank_name: this.employee.bank_name || '',
          account_type: this.employee.account_type || 'Corriente',
          account_number: this.employee.account_number || '',
          payment_identification: this.employee.payment_identification || '',
          status: this.employee.status || 'active',
          notes: this.employee.notes || '',
        }
      }
      return {
        name: '',
        identification: '',
        phone: '',
        email: '',
        location: '',
        role_or_job: '',
        event_name: '',
        type_salary: 'shift',
        rate: 0,
        bank_name: '',
        account_type: 'Corriente',
        account_number: '',
        payment_identification: '',
        status: 'active',
        notes: '',
      }
    },

    handleSubmit() {
      this.$emit('submit', { ...this.form })
    },

    formatBs(val) {
      const num = (parseFloat(val) || 0) * (this.currentExchangeRate || 1)
      return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Bs.'
    },
  },
}
</script>
